// START IMPORTS
// Theme imports
import { CustomIcons, useTheme } from '@ltvco/refresh-lib/theme';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Star,
  Gavel,
  AccountCircle,
  Phone,
  FamilyRestroom,
  People,
  Work,
  TaskAlt,
} from '@mui/icons-material';

// Type imports
import type { Connection, NavLinkData, Owner } from '@ltvco/refresh-lib/v1';
// END IMPORTS

// START HELPER_FUNCTIONS
const relativesCount = (relatives: Connection[]) => {
  let numRelatives = 0;

  relatives?.forEach((relative: Connection) => {
    numRelatives += 1;
    if (relative?.relatives?.length) {
      numRelatives += relative?.relatives?.length;
    }
  });

  return numRelatives;
};

export const getPersonNavLinkData = (
  reportOwner: Owner,
  showCriminalData: boolean,
  showBankruptcyData: boolean,
  hideCriminalBJLData: boolean,
  reportUpgraded: boolean,
  bonusDataLength: number,
  filteredRelatives: Connection[]
) => {
  let criminalBJLNavLinks: NavLinkData[] = [];
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');

  if (!hideCriminalBJLData) {
    criminalBJLNavLinks = [
      {
        href: '#criminal-or-traffic-section',
        sectionId: '#criminal-or-traffic-section',
        text: 'Criminal or Traffic',
        reportItemsCount: reportOwner?.courts.criminal?.length || 0,
        showIcon: !showCriminalData,
        icon: <Star fontSize="small" />,
      },
      {
        href: '#bankruptcies-section',
        sectionId: '#bankruptcies-section',
        text: 'Bankruptcies',
        reportItemsCount: reportOwner?.courts.bankruptcy?.length || 0,
        showIcon: !showBankruptcyData,
        icon: <Star fontSize="small" />,
      },
    ];
  }

  let ObituaryDataLink: NavLinkData[] = [];

  if (reportOwner?.obituary?.funeralHome) {
    ObituaryDataLink = [
      {
        href: '#obituary-section',
        sectionId: '#obituary-section',
        text: 'Obituary Details',
        hideReportItemsCount: true,
        reportItemsCount: null,
      },
    ];
  }

  let GraveDataLink: NavLinkData[] = [];

  if (reportOwner?.grave) {
    GraveDataLink = [
      {
        href: '#burial-information-section',
        sectionId: '#burial-information-section',
        text: 'Burial Information',
        hideReportItemsCount: true,
        reportItemsCount: null,
      },
    ];
  }

  return [
    {
      href: '#person-overview',
      sectionId: '#person-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    ...ObituaryDataLink,
    ...GraveDataLink,
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Phone Numbers',
      reportItemsCount: reportOwner.phones.length,
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Email Addresses',
      reportItemsCount: reportOwner.emails.length,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isInPartialAddressesVariation
        ? 'Possible Locations'
        : 'Address History',
      reportItemsCount: reportOwner.addresses.length,
      notificationSection: 'addresses',
    },
    {
      href: '#names-ancestry-section',
      sectionId: '#names-ancestry-section',
      text: 'Names & Ancestry',
      reportItemsCount: reportOwner?.identity?.names.length ? 1 : 0,
    },
    {
      href: '#relatives-section',
      sectionId: '#relatives-section',
      text: 'Possible Relatives',
      reportItemsCount: relativesCount(filteredRelatives) || 0,
      notificationSection: 'relatives',
    },
    {
      href: '#associates-section',
      sectionId: '#associates-section',
      text: 'Associates',
      reportItemsCount: reportOwner?.associates?.length || 0,
      notificationSection: 'associates',
    },
    {
      href: '#neighbors-section',
      sectionId: '#neighbors-section',
      text: 'Neighbors',
      reportItemsCount: reportOwner?.neighbors?.length || 0,
      notificationSection: 'neighbors',
    },
    ...criminalBJLNavLinks,
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Jobs & Education',
      reportItemsCount: reportOwner.jobs.length + reportOwner.educations.length,
      notificationSection: 'jobs',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social Media',
      reportItemsCount:
        reportOwner.profiles.length + reportOwner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount:
        reportOwner?.ownedAssets?.vehicles?.aircraft?.length +
          reportOwner?.ownedAssets?.vehicles?.watercraft?.length +
          reportOwner?.ownedAssets?.vehicles?.automobiles?.length +
          reportOwner?.ownedAssets?.real_estate?.length || 0,
      notificationSection: 'real_estate',
    },
    {
      href: '#additional-owner-details-section',
      sectionId: '#additional-owner-details-section',
      text: 'Free Bonus Data',
      reportItemsCount: bonusDataLength || 0,
      showIcon: !reportUpgraded,
      icon: <Star fontSize="small" />,
    },
  ];
};

function getCourtRecordsItem(reportOwner: Owner, reportUpgraded: boolean) {
  const licensesCount =
    (reportOwner.bonusData?.licenses?.dea?.length ?? 0) +
    (reportOwner.bonusData?.licenses?.faa?.length ?? 0) +
    (reportOwner.bonusData?.licenses?.professional?.length ?? 0) +
    (reportOwner.bonusData?.licenses?.sporting?.length ?? 0) +
    (reportOwner.bonusData?.filings?.business?.length ?? 0) +
    (reportOwner.bonusData?.filings?.ucc?.length ?? 0) +
    (reportOwner.bonusData?.licenses?.weapon?.length ?? 0);

  const theme = useTheme();

  const criminalCounts = reportOwner?.courts?.criminal?.reduce(
    (sum, c) => sum + (c?.offenses?.length || 0),
    0
  );

  return [
    {
      ...(reportUpgraded ? {} : { href: '#court-records-section' }),
      labelText: 'Court Records',
      reportItemsCount: reportOwner?.courts.criminal?.length || 0,
      icons: [
        <Gavel
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
        <Star
          sx={{
            color: theme.palette.primary.main,
            fontSize: '1.4rem',
            marginLeft: 1,
          }}
        />,
      ],
      showIcon: !reportUpgraded,
      childItems: reportUpgraded
        ? [
            {
              href: '#criminal-or-traffic-section',
              labelText: 'Criminal or Traffic',
              reportItemsCount: criminalCounts,
            },
            {
              href: '#bankruptcies-section',
              labelText: 'Bankruptcies',
              reportItemsCount: reportOwner.courts.bankruptcy.length,
            },
            {
              href: '#licenses-and-permits-section',
              labelText: 'Licenses and Permits',
              reportItemsCount: licensesCount,
            },
          ]
        : [],
    },
  ];
}

export const getPersonNavLinkDataV2 = (
  reportOwner: Owner,
  hideCriminalBJLData: boolean,
  reportUpgraded: boolean,
  filteredRelatives: Connection[]
) => {
  const courtRecordsItem = !hideCriminalBJLData
    ? getCourtRecordsItem(reportOwner, reportUpgraded)
    : [];
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  const obituaryDetailsItem = reportOwner?.obituary?.funeralHome
    ? [
        {
          href: '#obituary-section',
          labelText: 'Obituary Details',
          reportItemsCount: null, // Assuming you want to hide the count or it's not applicable
        },
      ]
    : [];

  const burialInformationItem = reportOwner?.grave
    ? [
        {
          href: '#burial-information-section',
          labelText: 'Burial Information',
          reportItemsCount: null, // Assuming you want to hide the count or it's not applicable
        },
      ]
    : [];

  const theme = useTheme();
  return [
    {
      href: '#person-overview',
      labelText: 'Overview',
      icons: [
        <AccountCircle
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
        />,
      ],
    },
    {
      labelText: 'Contact Information',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: reportOwner.phones.length,
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: reportOwner.emails.length,
        },
        {
          href: '#address-history-section',
          labelText: isInPartialAddressesVariation
            ? 'Possible Locations'
            : 'Address History',
          reportItemsCount: reportOwner.addresses.length,
        },
        {
          href: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount:
            reportOwner.profiles.length + reportOwner.usernames.length,
        },
      ],
    },
    {
      labelText: 'Family & Ancestry',
      icons: [
        <FamilyRestroom
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#names-ancestry-section',
          labelText: 'Name Origin',
          reportItemsCount: reportOwner?.identity?.names.length ? 1 : 0,
        },
        {
          href: '#relatives-section',
          labelText: 'Possible Relatives',
          reportItemsCount: relativesCount(filteredRelatives) || 0,
        },
        ...obituaryDetailsItem,
        ...burialInformationItem,
      ],
    },
    {
      labelText: 'Associates',
      icons: [
        <People
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#associates-section',
          labelText: 'Associates',
          reportItemsCount: reportOwner?.associates?.length || 0,
        },
        {
          href: '#neighbors-section',
          labelText: 'Neighbors',
          reportItemsCount: reportOwner?.neighbors?.length || 0,
        },
      ],
    },
    ...courtRecordsItem,
    {
      labelText: 'Jobs & Education',
      icons: [
        <Work sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />,
      ],
      childItems: [
        {
          href: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: reportOwner.jobs.length,
        },
        {
          href: '#education-section',
          labelText: 'Education',
          reportItemsCount: reportOwner.educations.length,
        },
      ],
    },
    {
      labelText: 'Assets',
      icons: [<CustomIcons.OwnedAssetsIcon color="primary" />],
      href: '#possible-owned-assets-section',
      reportItemsCount:
        reportOwner?.ownedAssets?.vehicles?.aircraft?.length +
          reportOwner?.ownedAssets?.vehicles?.watercraft?.length +
          reportOwner?.ownedAssets?.vehicles?.automobiles?.length +
          reportOwner?.ownedAssets?.real_estate?.length || 0,
    },
    {
      labelText: 'Next Steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
